import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig, TourStep } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";

export const useKCSChatbotTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const {
    setHighlightedItemKey,
    setActiveTourModule,
    setCurrentTourStep,
    hoverTooltipsEnabled,
  } = useTourGuideContext();
  const { suggestedQuestionsData, faqsData } = useMainChatContext();

  // Tour guide refs
  const textareaRef = useRef<HTMLDivElement>(null);
  const suggestedQuestionsRef = useRef<HTMLDivElement>(null);

  const isKCSChatbot =
    pathname === ROUTE_NAMES.KCS || pathname.includes("/kcs-chat");

  // Check if step 3 should be included (if suggested questions or FAQs exist)
  const hasSuggestedQuestionsOrFaqs =
    (suggestedQuestionsData?.rows &&
      suggestedQuestionsData?.rows?.length > 0) ||
    (faqsData?.rows && faqsData?.rows?.length > 0);

  // Tour guide configuration for KCS chatbot
  const kcsChatbotTourConfig: TourGuideConfig = useMemo(() => {
    if (!isKCSChatbot) {
      return {
        moduleId: "kcsChatbot",
        steps: [],
        enabled: false,
      };
    }

    const steps: TourStep[] = [
      {
        id: "kcs-textarea",
        targetRef: textareaRef,
        title: t("tourGuide.kcsChatbot.step1.title") || "Ask KCS AI",
        description:
          t("tourGuide.kcsChatbot.step1.description") ||
          "Ask questions to KCS AI and get intelligent responses. This AI assistant helps you with various tasks and information.",
        position: "top" as const,
        offset: { x: 0, y: 0 },
      },
      {
        id: "kcs-chatbot-dropdown",
        targetSelector: '[data-tour-item="chatbot-dropdown"]',
        title:
          t("tourGuide.kcsChatbot.step2.title") ||
          "Upload File or Switch Modes",
        description:
          t("tourGuide.kcsChatbot.step2.description") ||
          "Click the plus icon to upload files or switch between different chat modes. Access additional options and features.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
    ];

    // Add step 3 (suggested questions) only if data exists
    if (hasSuggestedQuestionsOrFaqs) {
      steps.push({
        id: "kcs-suggested-questions",
        targetRef: suggestedQuestionsRef,
        title: t("tourGuide.kcsChatbot.step3.title") || "Suggested Questions",
        description:
          t("tourGuide.kcsChatbot.step3.description") ||
          "Browse suggested questions and FAQs to get started quickly. Click on any question to use it as your query.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      });
    }

    return {
      moduleId: "kcsChatbot",
      enabled: true,
      steps,
    };
  }, [isKCSChatbot, t, hasSuggestedQuestionsOrFaqs]);

  const baseTourGuide = useTourGuide(kcsChatbotTourConfig);

  // Auto-start tour guide and handle completion tracking
  const kcsChatbotTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "kcs_tour_completed",
    isOnTourPath: isKCSChatbot,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (kcsChatbotTourGuide.isActive) {
      setActiveTourModule("kcsChatbot");
      setCurrentTourStep(kcsChatbotTourGuide.currentStep);
      setHighlightedItemKey(null);
    } else {
      setActiveTourModule(null);
      setCurrentTourStep(0);
      setHighlightedItemKey(null);
    }
  }, [
    kcsChatbotTourGuide.isActive,
    kcsChatbotTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for KCS chatbot tour guide
  const textareaStep = kcsChatbotTourConfig.steps.find(
    (step) => step.id === "kcs-textarea"
  );
  const chatbotDropdownStep = kcsChatbotTourConfig.steps.find(
    (step) => step.id === "kcs-chatbot-dropdown"
  );
  const suggestedQuestionsStep = kcsChatbotTourConfig.steps.find(
    (step) => step.id === "kcs-suggested-questions"
  );

  const textareaHover = useTourGuideHover("kcs-textarea", textareaStep || null);
  const chatbotDropdownHover = useTourGuideHover(
    "kcs-chatbot-dropdown",
    chatbotDropdownStep || null
  );
  const suggestedQuestionsHover = useTourGuideHover(
    "kcs-suggested-questions",
    suggestedQuestionsStep || null
  );

  // Attach hover handlers to chatbot dropdown via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      kcsChatbotTourGuide.isActive ||
      !chatbotDropdownStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="chatbot-dropdown"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!chatbotDropdownHover.tooltipData) return;
        chatbotDropdownHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        chatbotDropdownHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    kcsChatbotTourGuide.isActive,
    chatbotDropdownStep,
    chatbotDropdownHover.hoverProps,
    chatbotDropdownHover.tooltipData,
  ]);

  return {
    textareaRef,
    suggestedQuestionsRef,
    kcsChatbotTourGuide,
    kcsChatbotTourConfig,
    isKCSChatbot,
    textareaHover,
    chatbotDropdownHover,
    suggestedQuestionsHover,
  };
};
